import { FC, ReactNode, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router";
import { Button, Division, EmptyState, Heading, Icons, Image } from "@ir/ui";
import { useAuth } from "@/hooks";
import { WorkspaceProvider } from "@/providers/workspace";
import { UserProvider } from "@/providers/user";
import LoadingSpinner from "@/presentation/ui/components/common/LoadingSpinner";
import { GetUserDetailsUseCase } from "@/core/usecases/auth";
import { AuthRepository } from "@/infrastructure/repositories/auth";
import { assetsImages } from "@/assets";
import { BrandKeyHandler } from "./BrandKeyHandler";

interface AuthGuardProps {
  children: ReactNode;
}

export function AppAuthGuard({ children }: Readonly<AuthGuardProps>) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isAuthenticated === null || isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated === false) {
    return <GetUserWorkspace>{children}</GetUserWorkspace>;
  }

  return (
    <UserProvider>
      <BrandKeyHandler>
        <WorkspaceProvider>{children}</WorkspaceProvider>
      </BrandKeyHandler>
    </UserProvider>
  );
}

const GetUserWorkspace: FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth0();
  const { logout } = useAuth();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessageText, setErrorMessageText] = useState("");

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    // Check for brand_key in URL query params
    const searchParams = new URLSearchParams(location.search);
    const brandKey = searchParams.get("brand_key");

    new GetUserDetailsUseCase(new AuthRepository())
      .execute(brandKey || undefined)
      .then(result => {
        if (result.type === "success") {
          // Remove brand_key from URL if present
          if (brandKey) {
            searchParams.delete("brand_key");
            const newSearch = searchParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            globalThis.history.replaceState({}, "", newUrl);
          }
          globalThis.location.reload();
        } else {
          // Check if error is due to invalid brand_key
          if (brandKey) {
            setErrorMessageText(
              "The brand is invalid or you do not have permission to access this page."
            );
          } else {
            setErrorMessageText(result.errorMessage);
          }
          setIsLoading(false);
        }
      })
      .catch(error => {
        setErrorMessageText(error.message);
        setIsLoading(false);
      });
  }, [user, location.search, location.pathname]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (errorMessageText) {
    return (
      <Division className="d-flex flex-1 flex-align-center flex-justify-center">
        <Division>
          <Division className="d-flex flex-align-center flex-justify-center pb-4">
            <Image style={{ width: "220px" }} src={assetsImages.IRLogoFull} alt="IR Logo" />
          </Division>
          <EmptyState
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                412 - Error processing request
              </Heading>
            }
            description={errorMessageText}
            icon={Icons.InfoOctagon}
            className="p-4"
          >
            <Button
              onPress={() => {
                logout();
              }}
            >
              Retry
            </Button>
          </EmptyState>
        </Division>
      </Division>
    );
  }

  return children;
};
